import { useEffect, useRef, useState } from "react";
import { ImageUp, KeyRound, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, BACKEND_URL, formatDate, money } from "@/lib/api";
import { ADMIN_NAV } from "./nav";

const PLAN_STATUS = {
  active: { label: "Active", className: "bg-emerald-100 text-emerald-900" },
  complimentary: { label: "Comped", className: "bg-blue-100 text-blue-900" },
  grace: { label: "Past due", className: "bg-amber-100 text-amber-900" },
  unpaid: { label: "Unpaid", className: "bg-amber-100 text-amber-900" },
  locked: { label: "Locked", className: "bg-red-100 text-red-900" },
  pending_deletion: { label: "Delete pending", className: "bg-red-100 text-red-900" },
};

const blank = {
  name: "",
  slug: "",
  logo_url: "",
  contact_name: "",
  contact_email: "",
  active: true,
  website_url: "",
  public_description: "",
  plan_code: "partner",
  comped: false,
  paid_through: "",
};

export default function AdminPartners() {
  const logoRef = useRef(null);
  const [partners, setPartners] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [userForm, setUserForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [planForm, setPlanForm] = useState(null);
  const [plans, setPlans] = useState([]);
  const [savingPlan, setSavingPlan] = useState(false);

  const load = async () => {
    const [p, u] = await Promise.all([
      api.get("/admin/partners"),
      api.get("/admin/partner-users"),
    ]);
    setPartners(p.data);
    setUsers(u.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
    api
      .get("/admin/partner-plans")
      .then(({ data }) => setPlans(data.plans))
      .catch(() => {});
  }, []);

  const openPlan = (p) => {
    const s = p.subscription || {};
    setPlanForm({
      partner_id: p.id,
      partner_name: p.name,
      plan_code: s.plan_code || "partner",
      comped: !!s.comped,
      paid_through: s.paid_through || "",
      student_limit_override: s.limit_overridden ? String(s.student_limit) : "",
      active_students: s.active_students ?? p.student_count,
    });
  };

  const savePlan = async () => {
    setSavingPlan(true);
    try {
      await api.put(`/admin/partners/${planForm.partner_id}/subscription`, {
        plan_code: planForm.plan_code,
        comped: planForm.comped,
        paid_through: planForm.paid_through || null,
        student_limit_override: planForm.student_limit_override
          ? Number(planForm.student_limit_override)
          : null,
      });
      toast.success("Plan updated");
      setPlanForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingPlan(false);
    }
  };

  const save = async () => {
    try {
      const payload = { ...form };
      delete payload.logo_url;
      if (editingId) await api.put(`/admin/partners/${editingId}`, payload);
      else {
        const { data } = await api.post("/admin/partners", payload);
        setEditingId(data.id);
      }
      toast.success(editingId ? "Partner updated" : "Partner created");
      if (editingId) setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!editingId) {
      toast.error("Save the partner first, then upload its logo");
      return;
    }
    setUploadingLogo(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(`/admin/partners/${editingId}/logo`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({ ...form, logo_url: data.logo_url });
      toast.success("Logo uploaded");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploadingLogo(false);
      if (logoRef.current) logoRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!editingId) return;
    const { data } = await api.delete(`/admin/partners/${editingId}/logo`);
    setForm({ ...form, logo_url: data.logo_url });
    toast.success("Logo removed");
    load();
  };

  const toggleActive = async (partner) => {
    try {
      await api.put(`/admin/partners/${partner.id}`, { ...partner, active: !partner.active });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/partners/${confirmDelete.id}`);
      toast.success("Partner deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveUser = async () => {
    try {
      await api.post("/admin/partner-users", userForm);
      toast.success("Partner login created");
      setUserForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell
      nav={ADMIN_NAV}
      title="Partners"
      subtitle="Educational partners and their portal logins"
      actions={
        <Button
          className="gap-2"
          onClick={() => {
            setForm({ ...blank });
            setEditingId(null);
          }}
          data-testid="add-partner-button"
        >
          <Plus className="h-4 w-4" /> Add partner
        </Button>
      }
    >
      {!partners ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      ) : (
        <div className="space-y-8">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table data-testid="partners-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Partner</TableHead>
                    <TableHead>Enrollment path</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Students</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.map((p) => (
                    <TableRow key={p.id} data-testid={`partner-row-${p.slug}`}>
                      <TableCell className="font-medium text-slate-900">
                        <div className="flex items-center gap-3">
                          {p.logo_url && (
                            <img
                              src={`${BACKEND_URL}${p.logo_url}`}
                              alt=""
                              className="h-8 w-8 rounded object-contain"
                            />
                          )}
                          {p.name}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-blue-700">/{p.slug}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {p.contact_name || "—"}
                        <div className="text-xs text-slate-400">{p.contact_email}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <button
                          type="button"
                          className="text-left"
                          onClick={() => openPlan(p)}
                          data-testid={`manage-plan-${p.slug}`}
                        >
                          <span className="font-medium text-slate-900">
                            {p.subscription?.plan_name || "Partner"}
                          </span>
                          <span
                            className={`ml-2 rounded px-1.5 py-0.5 text-xs ${
                              PLAN_STATUS[p.subscription?.status]?.className ||
                              "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {PLAN_STATUS[p.subscription?.status]?.label || "—"}
                          </span>
                          <div className="text-xs text-slate-400">
                            {p.subscription?.comped
                              ? "no charge"
                              : !p.subscription?.paid_through
                              ? "awaiting payment"
                              : p.subscription.days_left >= 0
                              ? `renews ${formatDate(p.subscription.paid_through)}`
                              : `was due ${formatDate(p.subscription.paid_through)}`}
                          </div>
                        </button>
                      </TableCell>
                      <TableCell>
                        <span
                          className={
                            p.subscription?.at_limit
                              ? "font-semibold text-red-700"
                              : p.subscription?.near_limit
                              ? "font-semibold text-amber-700"
                              : ""
                          }
                          data-testid={`partner-usage-${p.slug}`}
                        >
                          {p.student_count} / {p.subscription?.student_limit ?? "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={p.active}
                          onCheckedChange={() => toggleActive(p)}
                          data-testid={`partner-active-${p.slug}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setForm({
                                name: p.name,
                                slug: p.slug,
                                logo_url: p.logo_url || "",
                                contact_name: p.contact_name || "",
                                contact_email: p.contact_email || "",
                                active: p.active,
                                website_url: p.website_url || "",
                                public_description: p.public_description || "",
                                plan_code: p.subscription?.plan_code || "partner",
                                comped: !!p.subscription?.comped,
                                paid_through: p.subscription?.paid_through || "",
                              });
                              setEditingId(p.id);
                            }}
                            data-testid={`edit-partner-${p.slug}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setUserForm({ name: "", email: "", password: "", partner_id: p.id })}
                            data-testid={`add-login-${p.slug}`}
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => setConfirmDelete(p)}
                            data-testid={`delete-partner-${p.slug}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-900">Partner logins</h2>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() =>
                    setUserForm({ name: "", email: "", password: "", partner_id: partners[0]?.id || "" })
                  }
                  data-testid="add-partner-user-button"
                >
                  <Plus className="h-4 w-4" /> New login
                </Button>
              </div>
              <Table data-testid="partner-users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Partner</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-sm text-slate-500">
                        No partner logins yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                      <TableCell>{u.partner_name}</TableCell>
                      <TableCell>
                        <Switch
                          checked={u.active !== false}
                          onCheckedChange={async (v) => {
                            await api.put(`/admin/partner-users/${u.id}`, { active: v });
                            load();
                          }}
                          data-testid={`user-active-${u.email}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={async () => {
                            await api.delete(`/admin/partner-users/${u.id}`);
                            toast.success("Login removed");
                            load();
                          }}
                          data-testid={`delete-user-${u.email}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={Boolean(planForm)} onOpenChange={(o) => !o && setPlanForm(null)}>
        <DialogContent data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>{planForm?.partner_name} — plan & renewal</DialogTitle>
          </DialogHeader>
          {planForm && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Plan (this sets the student limit)</Label>
                <Select
                  value={planForm.plan_code}
                  onValueChange={(v) => setPlanForm({ ...planForm, plan_code: v })}
                >
                  <SelectTrigger data-testid="plan-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((pl) => (
                      <SelectItem key={pl.code} value={pl.code}>
                        {pl.name} — {money(pl.price)}/yr · up to{" "}
                        {pl.student_limit.toLocaleString()} students
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {planForm.active_students} active students today.
                </p>
              </div>
              <div className="flex items-center justify-between gap-4 rounded-md bg-slate-50 px-4 py-3">
                <div>
                  <Label htmlFor="comped">Complimentary (no charge)</Label>
                  <p className="mt-1 text-xs text-slate-500">
                    Never billed, never locked, never reminded.
                  </p>
                </div>
                <Switch
                  id="comped"
                  checked={planForm.comped}
                  onCheckedChange={(v) => setPlanForm({ ...planForm, comped: v })}
                  data-testid="plan-comped-switch"
                />
              </div>
              <div className="space-y-2">
                <Label>Paid through / renewal date</Label>
                <Input
                  type="date"
                  value={planForm.paid_through ? planForm.paid_through.slice(0, 10) : ""}
                  onChange={(e) => setPlanForm({ ...planForm, paid_through: e.target.value })}
                  data-testid="plan-paid-through"
                />
              </div>
              <div className="space-y-2">
                <Label>Student limit override (optional)</Label>
                <Input
                  type="number"
                  placeholder="Leave blank to use the plan limit"
                  value={planForm.student_limit_override}
                  onChange={(e) =>
                    setPlanForm({ ...planForm, student_limit_override: e.target.value })
                  }
                  data-testid="plan-limit-override"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanForm(null)}>
              Cancel
            </Button>
            <Button onClick={savePlan} disabled={savingPlan} data-testid="save-plan-button">
              {savingPlan && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={Boolean(form)} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit partner" : "Add partner"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Partner name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="partner-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>URL slug</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  data-testid="partner-slug-input"
                />
                <p className="text-xs text-slate-500">
                  Enrollment page: partners.alabamachurchschool.com/{form.slug || "slug"}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Partner logo</Label>
                <div className="flex items-center gap-4">
                  {form.logo_url ? (
                    <img
                      src={`${BACKEND_URL}${form.logo_url}`}
                      alt="Partner logo"
                      className="h-14 w-14 rounded-md border border-slate-200 object-contain p-1"
                      data-testid="partner-logo-preview"
                    />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-md border border-dashed border-slate-300 text-[10px] text-slate-400">
                      No logo
                    </div>
                  )}
                  <input
                    ref={logoRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                    data-testid="partner-logo-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    disabled={uploadingLogo}
                    onClick={() => logoRef.current?.click()}
                    data-testid="partner-logo-upload-button"
                  >
                    {uploadingLogo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImageUp className="h-4 w-4" />
                    )}
                    Upload logo
                  </Button>
                  {form.logo_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={removeLogo}
                      data-testid="partner-logo-remove-button"
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  PNG, JPG, WEBP or SVG up to 3MB.
                  {!editingId && " Save the partner first to attach a logo."}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Contact name</Label>
                <Input
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  data-testid="partner-contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact email</Label>
                <Input
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  data-testid="partner-contact-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Website (shown in the public partner directory)</Label>
                <Input
                  placeholder="theirschool.com"
                  value={form.website_url || ""}
                  onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                  data-testid="partner-form-website"
                />
              </div>
              <div className="space-y-2">
                <Label>Public description</Label>
                <Textarea
                  rows={3}
                  maxLength={400}
                  value={form.public_description || ""}
                  onChange={(e) => setForm({ ...form, public_description: e.target.value })}
                  data-testid="partner-form-description"
                />
                <p className="text-xs text-slate-500">
                  Partners can edit this themselves; you can correct or clear it here. Only active,
                  current partners appear in the directory.
                </p>
              </div>
              <div className="space-y-2 border-t border-slate-100 pt-4">
                <Label>Plan</Label>
                <Select
                  value={form.plan_code || "partner"}
                  onValueChange={(v) => setForm({ ...form, plan_code: v })}
                >
                  <SelectTrigger data-testid="partner-form-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((pl) => (
                      <SelectItem key={pl.code} value={pl.code}>
                        {pl.name} — {money(pl.price)}/yr · up to{" "}
                        {pl.student_limit.toLocaleString()} students
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Until the annual registration is paid (or marked complimentary), this partner
                  cannot upload records or add students.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={!!form.comped}
                  onCheckedChange={(v) => setForm({ ...form, comped: v })}
                  data-testid="partner-form-comped"
                />
                <span className="text-sm text-slate-700">Complimentary (no charge)</span>
              </div>
              <div className="space-y-2">
                <Label>Paid through (optional)</Label>
                <Input
                  type="date"
                  value={form.paid_through ? form.paid_through.slice(0, 10) : ""}
                  onChange={(e) => setForm({ ...form, paid_through: e.target.value })}
                  data-testid="partner-form-paid-through"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="partner-form-active"
                />
                <span className="text-sm text-slate-700">Active</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button onClick={save} data-testid="save-partner-button">
              Save partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(userForm)} onOpenChange={(o) => !o && setUserForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create partner login</DialogTitle>
          </DialogHeader>
          {userForm && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Staff name</Label>
                <Input
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Temporary password (min 8 characters)</Label>
                <Input
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Partner</Label>
                <Select
                  value={userForm.partner_id}
                  onValueChange={(v) => setUserForm({ ...userForm, partner_id: v })}
                >
                  <SelectTrigger data-testid="user-partner-select">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {(partners || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserForm(null)}>
              Cancel
            </Button>
            <Button onClick={saveUser} data-testid="save-user-button">
              Create login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete partner?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This permanently deletes {confirmDelete?.name} and its portal logins. This cannot be
            undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={remove}
              data-testid="confirm-delete-partner"
            >
              Delete partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
